import $ from "jquery";

import {parseJobIdResponse} from "../api/parseResponse";
import ActionTypes from "./actionTypes";
import {getLocalStorageItem} from "./storageFunctions";
import {
  getJobLocalStorageKey,
  LOCAL_STORAGE_JOB_VERSION,
  resetJobLocalStorageLastSaved,
  setJobLocalStorageSaveBlocked,
  updateLocalStorage
} from "./jobLocalStorageSave";
import store from "../store";
import {requestAfterRoomChange} from "../bvcomponents/look/lookNavigation/lookNavigationFunctions";
import {switchToWizardItem} from "../bvcomponents/qkbPages/materialsWizard/useChangeWizardStepRoute";
import {LocalStorageJobData, RestoringJobData} from "../interfaces/RestoringJobData";
import {setNewActiveQuote, updateCurrentQuote} from "./quotesFunctions";
import {getCurrentLayout} from "../bvcomponents/appLayout";

export const isLocalStorageJobData = (value: any): value is LocalStorageJobData => {
  return !!value && typeof value === "object"
    && value.localStorageVersion === LOCAL_STORAGE_JOB_VERSION
    && Array.isArray(value.records)
    && value.records.some(record => record && record.data);
};

/**
 * removes the stored session (e.g. the user answered "No" on the restore question)
 */
export const removeJobFromLocalStorage = (localStorageKey: string = getJobLocalStorageKey()) => {
  try {
    window.localStorage.removeItem(localStorageKey);
  } catch (ex) { console.log("localStorage removeItem exception", ex); }
  resetJobLocalStorageLastSaved();
};

/**
 * Returns the stored session, sessions in an old/unknown format are not supported - they are removed
 */
export const getJobFromLocalStorage = (localStorageKey: string = getJobLocalStorageKey()): LocalStorageJobData | null => {
  const stored = getLocalStorageItem(localStorageKey);
  if (!stored) {
    return null
  }
  if (!isLocalStorageJobData(stored)) {
    removeJobFromLocalStorage(localStorageKey);
    return null
  }
  return stored
};

const isMaterialsPage = () => /^#\/materials(\/|\?|$)/.test(window.location.hash);

/**
 * On the materials page with the active wizard - applies the restored wizard: the last active wizard of the room
 * (see getDefaultMaterialsWizardItem), its selected values and the last visited step (the route is updated too)
 */
const restoreWizardOnMaterialsPage = () => {
  const {wizardIsActive} = store.getState().wizardState;
  if (wizardIsActive && isMaterialsPage()) {
    switchToWizardItem(null);
  }
};

/**
 * parseJobIdResponse restores everything into the currently opened visualizer (the initial one, the localStorage
 * session is offered on the first load). A saved job is opened directly in its visualizer, but the stored session
 * can have the last active quote in another visualizer (e.g. a kitchen quote while the bathroom is opened) -
 * then we switch to it the same way as the quotes switcher does (setNewActiveQuote -> switchToNewVisualizerType)
 * @returns {Promise<boolean>} true if the visualizer was switched
 */
const switchToRestoredActiveQuoteVisualizer = async (): Promise<boolean> => {
  const {quotes, activeQuoteIndex} = store.getState().quotesState;
  const activeQuote = quotes[activeQuoteIndex];
  if (!activeQuote || !activeQuote.room || (activeQuote.room === document.kvConfig.visualizerType)) {
    return false
  }
  // pass a copy - switchToNewVisualizerType updates the active quote from the new (not restored yet) layout,
  // setNewActiveQuote has to apply the restored bvState
  await setNewActiveQuote(activeQuoteIndex, [...quotes]);
  return activeQuote.room === document.kvConfig.visualizerType
};

/**
 * Restores the session stored by scheduleJobLocalStorageSave, the same way as a saved job is restored
 * @returns {Promise<boolean>} true if there was something to restore
 */
export const restoreJobFromLocalStorage = async (): Promise<boolean> => {
  const stored = getJobFromLocalStorage();
  if (!stored) {
    return false
  }
  setJobLocalStorageSaveBlocked(true); // don't overwrite the stored session in the middle of the restore
  try {
    const jobData: RestoringJobData = {
      ...stored,
      records: stored.records.filter(record => record && record.data),
    };
    await parseJobIdResponse(jobData);
    const visualizerSwitched = await switchToRestoredActiveQuoteVisualizer();
    if (!visualizerSwitched) {
      await requestAfterRoomChange(getCurrentLayout()); // the same as setItemsFromBvState does (it's done there on switch)
    } else {
      await updateCurrentQuote(); // the restored active quote image was taken from the previous visualizer canvas
    }
    restoreWizardOnMaterialsPage();
    $(window).trigger(ActionTypes.UPDATE_ALL_ITEMS);
  } finally {
    setJobLocalStorageSaveBlocked(false);
  }
  resetJobLocalStorageLastSaved();
  updateLocalStorage();
  return true
};
